import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';

// 공통 API 응답 타입
export interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
  code?: number;
  status?: number;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;
if (!API_BASE_URL) {
  console.warn('NEXT_PUBLIC_API_URL is not defined');
}

export const baseAPI = axios.create({
  baseURL: API_BASE_URL
    ? `${API_BASE_URL}/v1`
    : 'http://119.65.255.162:18091/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// 공통 응답 변환 함수
export function transformResponse<T>(
  response: AxiosResponse<{ code: number; message: string; data: T }>,
): ApiResponse<T> {
  return {
    status: response.status,
    message: response.statusText,
    data: response.data.data,
  };
}

// 서버 시각 캐시 (응답 Date 헤더 기준). 클라이언트 시계 대신 서버 기준 비교용
let serverTimeCache: Date | null = null;

/** 서버에서 받은 시각 반환. API 응답이 한 번이라도 있으면 그 Date 헤더 기준, 없으면 로컬 시각 */
export function getServerTime(): Date {
  return serverTimeCache ?? new Date();
}

// 응답 인터셉터: 서버 Date 헤더 저장 + 에러 핸들링
baseAPI.interceptors.response.use(
  (response: AxiosResponse) => {
    const dateHeader = response.headers?.['date'];
    if (dateHeader) {
      const parsed = new Date(dateHeader);
      if (!Number.isNaN(parsed.getTime())) serverTimeCache = parsed;
    }
    return response;
  },
  (error: AxiosError<ApiError>) => {
    if (error.response) {
      throw {
        error: error.response.data.error || 'API_ERROR',
        message: error.response.data.message || '서버 에러가 발생했습니다.',
        details: error.response.data.details,
        code: error.response.data.code,
        status: error.response.status,
      };
    } else if (error.request) {
      throw {
        error: 'NETWORK_ERROR',
        message: '네트워크 연결에 실패했습니다.',
      };
    } else {
      throw {
        error: 'REQUEST_ERROR',
        message: '요청을 보내는데 실패했습니다.',
      };
    }
  },
);

// fetcher (GET)
// export const fetcher = async <T>(
//   url: string,
//   config?: AxiosRequestConfig,
// ): Promise<ApiResponse<T>> => {
//   const res = await baseAPI.get(url, config);
//   return transformResponse<T>(res);
// };

export const fetcher = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const res = await baseAPI.get(url, {
    ...config,
    paramsSerializer: (params) => {
      // 배열을 keywords=direct&keywords=discount_event 형태로 직렬화
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });
  return transformResponse<T>(res);
};

// fetcher (POST)
export const postFetcher = async <T, D = unknown>(
  url: string,
  body: D,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const res = await baseAPI.post(url, body, config);
  return transformResponse<T>(res);
};

// 쿼리 키 상수
export const QUERY_KEYS = {
  BANNER: 'banner',
  PROGRAMS: 'programs',
  PROGRAM_GROUP: 'program-group',
  PROGRAM_DETAIL: 'program-detail',
  PROGRAM_BANNERS: 'program-banners',
  ANALYSIS: 'analysis',
  ANALYSIS_RECOMMENDATIONS: 'analysis-recommendations',
  EVENTS: 'events',
  EVENT_DETAIL: 'event-detail',
  NOTICES: 'notices',
  NOTICE_DETAIL: 'notice-detail',
  TERMS_OF_USE: 'terms-of-use',
  PRIVACY_POLICY: 'privacy-policy',
  ARTICLE: 'article',
  REGION: 'region',
  STORE: 'store',
  STORE_REVIEW: 'store-review',
  STATISTIC: 'statistic',
  PRICING: 'pricing',
  LIST: 'list',
  DETAIL: 'detail',
  ACADEMY: 'academy',
  LICENSE_TEST_COURSE: 'license-test-course',
  LICENSE_TEST_COURSE_DETAIL: 'license-test-course-detail',
  LICENSE_TEST_COURSE_REGION: 'license-test-course-region',
  LICENSE_TEST_COURSE_ARTICLE_ACADEMIES:
    'license-test-course-article-academies',
  LICENSE_TEST_COURSE_ARTICLE_ACADEMY_BOARD_TYPES:
    'license-test-course-article-academy-board-types',
  CAMPAIGN_LANDING: 'campaign-landing',
} as const;
